import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  useToast,
} from '@elkaso-app/web-design';
import { useUploadFileApi } from 'apis/config/use-upload-file-api';
import { MasterProductFamily, useCreateMasterProductFamilyApi } from 'apis/master-product-families';
import type { CategoryRef } from 'pages/exclusive-deals/components/editable-deals-grid/editable-deal-row.types';
import { GridCategoryModal } from 'pages/exclusive-deals/components/editable-deals-grid/grid-category-modal';
import React, { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FiChevronDown } from 'react-icons/fi';

interface CreateFamilyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (family: MasterProductFamily) => void;
}

interface FormData {
  universalName: string;
}

export const CreateFamilyModal: React.FC<CreateFamilyModalProps> = ({ isOpen, onClose, onCreated }) => {
  const toast = useToast();
  const [category, setCategory] = useState<CategoryRef | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useUploadFileApi({
    onSuccess: (data: any) => {
      const url = data?.url || data?.fileUrl || data?.path || '';
      if (url) setImageUrl(url);
    },
  });
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const body = new FormData();
    body.append('file', file);
    upload.mutate({ body });
  };

  const { mutate: createFamily, isLoading: isCreating } = useCreateMasterProductFamilyApi({
    onSuccess: (data) => {
      toast({ title: `Family "${data.universalName}" created`, status: 'success' });
      reset();
      setCategory(null);
      setImageUrl('');
      onClose();
      onCreated?.(data);
    },
    onError: (error: any) => {
      const status = error?.response?.status;
      const message =
        status === 409
          ? 'A family with this name already exists.'
          : error?.response?.data?.message || 'Failed to create family. Please try again.';
      toast({ title: message, status: 'error' });
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<FormData>({ mode: 'onChange', defaultValues: { universalName: '' } });

  const onSubmit = useCallback(
    (data: FormData) => {
      if (!category) {
        toast({ title: 'Category is required', status: 'warning' });
        return;
      }
      createFamily({
        universalName: data.universalName.trim(),
        catalogCategoryId: category.id,
        imageUrl: imageUrl || undefined,
      });
    },
    [createFamily, category, imageUrl, toast]
  );

  const onCloseModal = useCallback(() => {
    reset();
    setCategory(null);
    setImageUrl('');
    onClose();
  }, [reset, onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCloseModal}
      size={{ base: 'full', md: 'lg' }}
      scrollBehavior='inside'
      closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New Family</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <FormControl isInvalid={!!errors.universalName} isRequired>
              <FormLabel>Universal Name</FormLabel>
              <Input
                placeholder='e.g. Whole Chicken'
                {...register('universalName', {
                  required: 'Universal name is required',
                  validate: (value) => value.trim().length > 0 || 'Universal name cannot be empty',
                })}
              />
              <FormErrorMessage>{errors.universalName?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Category</FormLabel>
              <GridCategoryModal
                value={category}
                onSelect={setCategory}
                trigger={
                  <Button
                    variant='outline'
                    w='100%'
                    justifyContent='space-between'
                    fontWeight='normal'
                    color={category ? 'gray.800' : 'gray.400'}
                    rightIcon={<FiChevronDown />}>
                    <Box as='span' isTruncated>
                      {category ? category.title : '— Select category —'}
                    </Box>
                  </Button>
                }
              />
            </FormControl>

            <FormControl>
              <FormLabel>Image</FormLabel>
              <Flex align='center' gap='3'>
                {imageUrl ? (
                  <Image src={imageUrl} alt='family' boxSize='56px' objectFit='cover' rounded='md' />
                ) : (
                  <Box boxSize='56px' bg='gray.100' rounded='md' />
                )}
                <input ref={fileRef} type='file' accept='image/*' hidden onChange={onFile} />
                <Button
                  size='sm'
                  variant='outline'
                  isLoading={upload.isLoading}
                  onClick={() => fileRef.current?.click()}>
                  {imageUrl ? 'Replace image' : 'Upload image'}
                </Button>
                {imageUrl && (
                  <Button size='sm' variant='ghost' colorScheme='red' onClick={() => setImageUrl('')}>
                    Remove
                  </Button>
                )}
                {upload.isLoading && <Spinner size='sm' color='gray.400' />}
              </Flex>
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant='outline' mr={3} onClick={onCloseModal} isDisabled={isCreating}>
            Cancel
          </Button>
          <Button
            colorScheme='red'
            onClick={handleSubmit(onSubmit)}
            isLoading={isCreating}
            loadingText='Creating...'
            isDisabled={!isValid || !category}>
            Create Family
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
